import { useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import { createNewChat, type JgsStudentData } from "../services/api";
import { useChatStream, StreamEvent } from "../hooks/useChatStream";
import {
  trackMessageComplete,
  trackChatContainerMounted,
  trackChatStarted,
  trackMessageSent,
  trackStopClicked,
  trackOutreachDraftStarted,
} from "../analytics/analytics";

interface ChatMessage {
  id: string;
  sender: "user" | "ai";
  text: string;
}

interface ChatContainerProps {
  onError?: (message: string) => void;
  onMessagesUpdate?: (threadId: string, messages: ChatMessage[]) => void;
  onComplete?: (threadId: string) => void;
  onReady?: () => void;
  onStudentInfo?: (jgsStudentData: JgsStudentData | null) => void;
  onStreamingChange?: (threadId: string, isStreaming: boolean) => void;
  onCancellingChange?: (threadId: string, isCancelling: boolean) => void;
  onTitleUpdate?: (threadId: string, title: string) => void;
  onProgress?: (message: string) => void;
  onOutreachDraft?: (outreach: {
    outreachId: string;
    outreachUrl: string;
    recipient: { coachName: string; coachEmail: string; schoolName: string };
    subject: string;
  }) => void;
  onProfileUpdated?: (fields: Record<string, unknown>) => void;
  initialThreadId?: string | null;
  initialMessages?: ChatMessage[];
  onThreadCreated?: (threadId: string) => void;
  // When provided, sends will target this thread (without stopping background streams)
  activeThreadIdForSends?: string | null;
  // For coaches: which JGS profile to use when creating new chats
  linkedJgsProfile?: string | null;
}

export interface ChatContainerHandle {
  send: (text: string) => Promise<void>;
  stop: () => Promise<void>;
  isStreaming: boolean;
  isCancelling: boolean;
}

export const ChatContainer = forwardRef<ChatContainerHandle, ChatContainerProps>(
  function ChatContainer(
    {
      onError,
      onMessagesUpdate,
      onComplete,
      onReady,
      onStudentInfo,
      onStreamingChange,
      onCancellingChange,
      onTitleUpdate,
      onProgress,
      onOutreachDraft,
      onProfileUpdated,
      initialThreadId = null,
      initialMessages = [],
      onThreadCreated,
      activeThreadIdForSends = null,
      linkedJgsProfile = null,
    }: ChatContainerProps,
    ref
  ) {
    const [threadId, setThreadId] = useState<string | null>(initialThreadId ?? null);
    const [messages, setMessages] = useState<ChatMessage[]>(initialMessages ?? []);
    const streamThreadIdRef = useRef<string | null>(threadId);
    const hasReceivedChunkRef = useRef(false);

    // Refs for callback props — ensures the SSE loop always calls the latest version
    const onMessagesUpdateRef = useRef(onMessagesUpdate);
    const onTitleUpdateRef = useRef(onTitleUpdate);
    const onCompleteRef = useRef(onComplete);
    const onProgressRef = useRef(onProgress);
    const onErrorRef = useRef(onError);
    const onOutreachDraftRef = useRef(onOutreachDraft);
    const onProfileUpdatedRef = useRef(onProfileUpdated);
    useEffect(() => {
      onMessagesUpdateRef.current = onMessagesUpdate;
      onTitleUpdateRef.current = onTitleUpdate;
      onCompleteRef.current = onComplete;
      onProgressRef.current = onProgress;
      onErrorRef.current = onError;
      onOutreachDraftRef.current = onOutreachDraft;
      onProfileUpdatedRef.current = onProfileUpdated;
    }, [
      onMessagesUpdate,
      onTitleUpdate,
      onComplete,
      onProgress,
      onError,
      onOutreachDraft,
      onProfileUpdated,
    ]);

    const { isStreaming, isCancelling, start, cancel } = useChatStream({
      onEvent: (evt: StreamEvent) => {
        switch (evt.type) {
          case "start":
            hasReceivedChunkRef.current = false;
            break;
          case "chunk":
            if (!hasReceivedChunkRef.current) {
              hasReceivedChunkRef.current = true;
              onProgressRef.current?.("");
            }
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last && last.sender === "ai") {
                const merged = prev.slice(0, -1).concat({ ...last, text: last.text + evt.content });
                onMessagesUpdateRef.current?.(streamThreadIdRef.current || "", merged);
                return merged;
              } else {
                const updated = prev.concat({
                  id: `ai-${Date.now()}`,
                  sender: "ai",
                  text: evt.content,
                });
                onMessagesUpdateRef.current?.(streamThreadIdRef.current || "", updated);
                return updated;
              }
            });
            break;
          case "title_update":
            // Bubble title updates up so the page can persist and reflect them immediately
            onTitleUpdateRef.current?.(evt.threadId || streamThreadIdRef.current || "", evt.title);
            break;
          case "outreach_draft":
            console.log("📧 Outreach draft event received:", evt.outreach);
            if (evt.outreach?._id) {
              trackOutreachDraftStarted({
                outreachId: evt.outreach._id,
                schoolName: evt.outreach?.recipient?.schoolName || "",
              });
            }
            onOutreachDraftRef.current?.(evt.outreach);
            break;
          case "profile_updated":
            console.log("📝 Profile updated event received:", evt.fields);
            onProfileUpdatedRef.current?.(evt.fields);
            break;
          case "progress":
            hasReceivedChunkRef.current = false;
            onProgressRef.current?.(evt.message);
            break;
          case "complete":
            trackMessageComplete();
            onCompleteRef.current?.(streamThreadIdRef.current || "");
            break;
          case "error":
            onErrorRef.current?.(evt.message || "Stream error");
            break;
        }
      },
    });

    // Notify parent of streaming state changes (in useEffect to avoid render-phase updates)
    useEffect(() => {
      // Use setTimeout to defer the callback to after render
      const timer = setTimeout(() => {
        onStreamingChange?.(streamThreadIdRef.current || "", isStreaming);
      }, 0);
      return () => clearTimeout(timer);
    }, [isStreaming, onStreamingChange]);

    // Notify parent of cancelling state changes
    useEffect(() => {
      const timer = setTimeout(() => {
        onCancellingChange?.(streamThreadIdRef.current || "", isCancelling);
      }, 0);
      return () => clearTimeout(timer);
    }, [isCancelling, onCancellingChange]);

    useEffect(() => {
      trackChatContainerMounted();
      // Signal readiness to the parent immediately to avoid UI race conditions
      onReady?.();

      // Authentication is now via httpOnly cookie, no need to check token
      // If a thread is provided, hydrate messages; otherwise, wait until first send to create
      if (initialThreadId) {
        console.log("♻️ Hydrating existing thread:", initialThreadId);
        setThreadId(initialThreadId);
        setMessages(Array.isArray(initialMessages) ? initialMessages : []);
      } // else: delay thread creation until first user message is sent
      // Cleanup: cancel any in-flight stream when unmounting the orchestrator
      return () => {
        try {
          void cancel();
        } catch {}
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const send = async (text: string) => {
      console.log("📨 ChatContainer.send called", {
        hasThreadId: !!threadId,
        text: text.substring(0, 50),
      });
      // Determine target thread for this send: prefer explicit activeThreadIdForSends
      let tidLocal: string | null = (activeThreadIdForSends ?? threadId) || null;
      if (!tidLocal || !text) {
        if (!text) {
          console.warn("⚠️ Cannot send - missing requirements:", { threadId, hasText: !!text });
          return;
        }
        // Lazily create a thread before first message
        try {
          const res = await createNewChat(linkedJgsProfile || undefined);
          console.log("✅ Chat created on first message:", res.threadId);
          setThreadId(res.threadId);
          tidLocal = res.threadId;
          trackChatStarted({ threadId: res.threadId, hasJgs: !!res.jgs_student_data });
          onThreadCreated?.(res.threadId);
          if (typeof onStudentInfo === "function") {
            onStudentInfo(res.jgs_student_data || null);
          }
        } catch (e: any) {
          console.error("❌ Failed to create chat on send:", e);
          onError?.(e?.message || "Failed to start chat");
          return;
        }
      }
      trackMessageSent({ length: text.length });
      // Reset state if switching target threads, then add user message
      if (tidLocal !== threadId) {
        setMessages([]);
        setThreadId(tidLocal);
      }
      streamThreadIdRef.current = tidLocal;
      setMessages(prev => {
        const updated = prev.concat({ id: `user-${Date.now()}`, sender: "user", text });
        onMessagesUpdate?.(tidLocal, updated);
        return updated;
      });
      // Start streaming AI reply
      console.log("🔄 Starting stream for message...");
      if (!tidLocal) {
        onError?.("No thread available to send message");
        return;
      }
      await start({ threadId: tidLocal, prompt: text });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stop = async () => {
      trackStopClicked({ threadId: threadId || "" });
      await cancel();
    };

    useImperativeHandle(ref, () => ({ send, stop, isStreaming, isCancelling }), [
      send,
      stop,
      isStreaming,
      isCancelling,
    ]);

    return null; // Headless orchestrator; integrate via props callbacks
  }
);
